import {
  Leaf,
  Megaphone,
  CloudRain,
  Trash,
  Drop,
  TreeEvergreen,
  Plant,
  Buildings,
  UsersThree,
  ShieldCheck,
  HandHeart,
  Handshake,
  FileText,
  SpeakerHigh,
  Warning,
  Users,
  GitBranch,
} from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const VOLUNTEER_IMG = "https://customer-assets.emergentagent.com/job_brief-builder-33/artifacts/efgcq4nw_PHOTO-2026-06-06-15-31-36.jpg";

const initiativeIcons = [Leaf, Megaphone, CloudRain, Trash, Drop, TreeEvergreen, Plant, Buildings, UsersThree, ShieldCheck];
const actionIcons = [HandHeart, Handshake, FileText, SpeakerHigh, Warning, Users, GitBranch];

export default function ContactPage() {
  const { t } = useLanguage();
  const initiatives = t("initiatives_list") || [];
  const actions = t("actions") || [];
  return (
    <div data-testid="contact-page">
      {/* Page Header */}
      <section
        className="py-20 md:py-28 border-b"
        style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-4"
            style={{ color: '#B85C38' }}
          >
            {t("contact_tagline")}
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium"
            style={{ color: '#1C211F' }}
          >
            {t("contact_heading")}
          </h1>
        </div>
      </section>

      {/* Initiatives Section */}
      <section
        data-testid="initiatives-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
            <div className="md:col-span-5">
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
                style={{ color: '#B85C38' }}
              >
                {t("initiatives_tagline")}
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-5"
                style={{ color: '#1C211F' }}
              >
                {t("initiatives_heading")}
              </h2>
              <p className="text-base leading-relaxed" style={{ color: '#4A5450' }}>
                {t("initiatives_desc")}
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {initiatives.map((label, i) => {
                  const Icon = initiativeIcons[i] || Leaf;
                  return (
                    <div
                      key={i}
                      data-testid={`initiative-item-${i}`}
                      className="flex items-center gap-3 p-4 rounded-lg border"
                      style={{
                        backgroundColor: '#F7F5F0',
                        borderColor: 'rgba(26, 92, 55, 0.1)',
                      }}
                    >
                      <Icon size={20} weight="duotone" style={{ color: '#1A5C37', flexShrink: 0 }} />
                      <span className="text-sm font-medium" style={{ color: '#1C211F' }}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section
        data-testid="get-involved-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#E8EDE9' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 mb-16">
            <div className="md:col-span-6">
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
                style={{ color: '#B85C38' }}
              >
                {t("get_involved_tagline")}
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-5"
                style={{ color: '#1C211F' }}
              >
                {t("get_involved_heading")}
              </h2>
              <p className="text-base leading-relaxed" style={{ color: '#4A5450' }}>
                {t("get_involved_desc")}
              </p>
            </div>
            <div className="md:col-span-6">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={VOLUNTEER_IMG}
                  alt="Community volunteering"
                  className="w-full h-56 md:h-72 object-cover"
                  data-testid="volunteer-image"
                />
              </div>
            </div>
          </div>

          {/* Action Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {actions.map((action, i) => {
              const Icon = actionIcons[i] || HandHeart;
              return (
                <div
                  key={i}
                  data-testid={`action-card-${i}`}
                  className="kybia-card p-6 rounded-lg border"
                  style={{
                    backgroundColor: '#F7F5F0',
                    borderColor: 'rgba(26, 92, 55, 0.1)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: 'rgba(184, 92, 56, 0.08)' }}
                  >
                    <Icon size={20} weight="duotone" style={{ color: '#B85C38' }} />
                  </div>
                  <h3
                    className="font-heading text-lg font-medium mb-2"
                    style={{ color: '#1C211F' }}
                  >
                    {action.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#4A5450' }}>
                    {action.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section
        data-testid="contact-info-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
            style={{ color: '#B85C38' }}
          >
            {t("contact_reach_tagline")}
          </p>
          <h2
            className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-5"
            style={{ color: '#1C211F' }}
          >
            {t("contact_reach_heading")}
          </h2>
          <p className="text-base leading-relaxed mb-8" style={{ color: '#4A5450' }}>
            {t("contact_reach_desc")}
          </p>
          <div
            className="inline-block px-8 py-4 rounded-lg border"
            style={{
              backgroundColor: '#F7F5F0',
              borderColor: 'rgba(26, 92, 55, 0.15)',
            }}
          >
            <p className="text-sm font-medium" style={{ color: '#1C211F' }}>
              info@kybia.org
            </p>
            <p className="text-xs mt-1" style={{ color: '#4A5450' }}>
              {t("contact_location")}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
